import type { Nullable } from "@msuite/utils/types";
import { cva, type VariantProps } from "class-variance-authority";

export const CALENDAR_HOUR_HEIGHT = 48;
export const CALENDAR_DAY_WIDTH = 200;
export const CALENDAR_DAY_HEADER_HEIGHT = 24;
export const CALENDAR_DAY_HOURS = 24;
export const CALENDAR_DAY_STEP_FACTOR = 60; // 15 minutes per step

export interface CalendarDate<T extends object> {
	date: string;
	items: CalendarItem<T>[];
}

export type CalendarItemDurationType = "all-day" | "timed";

export interface CalendarItem<T> {
	id: string;
	durationType: CalendarItemDurationType;
	startTime: Nullable<string>;
	endTime: Nullable<string>;
	firstDayIsHalfDay: boolean;
	lastDayIsHalfDay: boolean;
	_item: T;
}

export interface CalendarItemTimed<T extends object> extends CalendarItem<T> {
	durationType: "timed";
	startTime: string; // ISO time string
	endTime: string; // ISO time string
}

export interface CalendarItemAllDay<T extends object> extends CalendarItem<T> {
	durationType: "all-day";
	startTime: null;
	endTime: null;
}

export interface CalendarDayBaseProps<T extends object> {
	index: number;
	item: CalendarDate<T>;
}

/** Classes */
export const calendarItem = cva(
	[
		"px-2 w-full text-sm rounded-xs flex flex-col",
		"cursor-pointer active:opacity-90 transition-transform duration-100 ease-in-out",
	],
	{
		variants: {
			color: {
				orange: "bg-orange-500/85 dark:bg-orange-500/60 border border-orange-500",
				blue: "bg-blue-500/85 dark:bg-blue-500/60 border border-blue-500",
				emerald: "bg-emerald-500/85 dark:bg-emerald-500/60 border border-emerald-500",
				red: "bg-red-500/85 dark:bg-red-500/60 border border-red-500",
				purple: "bg-purple-500/85 dark:bg-purple-500/60 border border-purple-500",
				gray: "bg-gray-500/85 dark:bg-gray-500/60 border border-gray-500",
			},
			type: {
				"all-day": "py-1",
				timed: "py-2",
			},
		},
		defaultVariants: {
			color: "emerald",
		},
	},
);

export type CalendarItemColor = VariantProps<typeof calendarItem>["color"];
